import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { motion } from 'framer-motion';
import { showToast } from '../../utils/toastUtils';
import { setPricing, saveOnboardingState, setCurrentStep } from '../../redux/onboardingReducer';
import OnboardingButtonDock from '../../components/OnboardingButtonDock';
import { useTheme } from '@mui/material/styles';
const CheckIcon = ({ className }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  return (
    <svg 
      className={`${className} ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} 
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24" 
      xmlns="http://www.w3.org/2000/svg"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
};


const PricingCard = ({ title, price, minutes, features, onSelect, isSelected, isPremium }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  return (
    <motion.div 
      className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg overflow-hidden ${
        isPremium ? `border-3 ${isDarkMode ? 'border-yellow-400' : 'border-yellow-500'}` : ''
      } ${isSelected && isPremium ? `ring-2 ${isDarkMode ? 'ring-yellow-300' : 'ring-yellow-400'} shadow-2xl` : isSelected ? 'ring-2 ring-blue-500' : ''}`}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ 
        scale: isPremium ? 1.05 : 1.03,
        boxShadow: isPremium 
          ? isDarkMode 
            ? '0 20px 25px -5px rgba(234, 179, 8, 0.2), 0 10px 10px -5px rgba(234, 179, 8, 0.1)'
            : '0 20px 25px -5px rgba(234, 179, 8, 0.1), 0 10px 10px -5px rgba(234, 179, 8, 0.04)'
          : isDarkMode
            ? '0 20px 25px -5px rgba(255, 255, 255, 0.1), 0 10px 10px -5px rgba(255, 255, 255, 0.04)'
            : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        transition: {
          duration: 0.3,
          ease: "easeInOut"
        }
      }}
      onClick={() => onSelect(title.toLowerCase(), price)}
    >
      <div className={`px-10 pb-11 pt-5 ${isPremium ? isDarkMode ? 'bg-gradient-to-br from-yellow-900 to-yellow-800' : 'bg-gradient-to-br from-yellow-100 to-yellow-200' : ''}`}>
        <h2 className={`text-2xl font-bold mb-3 text-center ${isPremium ? isDarkMode ? 'text-yellow-300 text-3xl font-bold' : 'text-yellow-800 text-3xl font-bold' : isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
          {title}
        </h2>
        <p className={`${isPremium ? 'text-3xl' : 'text-3xl'} font-black mb-1 text-center ${isPremium ? isDarkMode ? 'text-yellow-200' : 'text-yellow-700' : isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          ₹{price.toLocaleString()}
          <span className={`${isPremium ? 'text-2xl' : 'text-xl'} font-bold ml-1`}>/ {minutes.toLocaleString()} mins</span>
        </p>
        <p className={`text-sm text-center font-bold  ${isPremium ? isDarkMode ? 'text-yellow-300' : 'text-yellow-600' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            (excluding GST)
          </p>
        <div className="pb-8">
          <motion.div>
            <ul className="space-y-2 text-sm pt-3">
              {features.map((feature, index) => (
                <motion.li 
                  key={index} 
                  className="flex items-start" 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  whileHover={isPremium ? { x: 5, transition: { duration: 0.2 } } : {}}
                >
                  <CheckIcon className={`w-6 h-5 mr-2 mt-1 flex-shrink-0 ${isPremium ? isDarkMode ? 'text-yellow-400' : 'text-yellow-600' : ''}`} />
                  <span className={`${isPremium ? isDarkMode ? 'text-base font-medium text-yellow-100' : 'text-base font-medium text-yellow-900' : isDarkMode ? 'text-base text-gray-300' : 'text-base text-gray-600'}`}>{feature}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

const OnboardingPricing = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [lastSavedPlan, setLastSavedPlan] = useState(null);
  const [savedPlans, setSavedPlans] = useState({});
  const onboardingState = useSelector(state => state.onboarding);
  const userIdFromRedux = onboardingState.userId;
  const userId = userIdFromRedux || localStorage.getItem('userId');
  const selectedPlan = onboardingState.pricing;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  useEffect(() => {
    const fetchSavedPlan = async () => {
      if (userId) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/plan?userId=${userId}`);
          if (response.data && response.data.planDetails) {
            const { name: plan, price } = response.data.planDetails;
            dispatch(setPricing({ plan, price }));
            setSavedPlans(prevSavedPlans => ({
              ...prevSavedPlans,
              [plan]: true
            }));
            setLastSavedPlan(plan);
          } else {
            selectPremiumPlan();
          }
        } catch (error) {
          console.error('Error fetching saved plan:', error);
          selectPremiumPlan();
        }
      } else {
        selectPremiumPlan();
      }
      setIsLoading(false);
    };

    const selectPremiumPlan = () => {
      const premiumPlan = plans.find(p => p.title === 'Premium');
      dispatch(setPricing({ plan: premiumPlan.title.toLowerCase(), price: premiumPlan.price }));
    };

    fetchSavedPlan();
  }, [userId, dispatch]);

  const handleSelectPlan = (plan, price) => {
    dispatch(setPricing({ plan, price }));
  };

  const features = [
    'Multilingual Call Handling Agent',
    'Self-Learning and Continuous Improvement',
    'Voice Customization',
    'Personality Customization',
    'Multiple Use Case Options',
    'Advance Actionable Agent',
    'Automated Appointment Scheduling',
    'Advanced Call Routing',
    'Sentiment Analysis',
    'Proactive Engagement',
    'Advanced AI Voice Support',
    'High Voice Recognition Accuracy',
    'Advanced Analytics and Reporting',
    'Comprehensive Logs and Monitoring',
    'Conversation Transcript',
    'Conversation Audio Recordings',
    'Email and Call Support',
    'Secure and Compliant'
  ];

  const plans = [
    {
      title: 'Basic',
      price: 4999,
      minutes: 1500,
      features: features
    },
    {
      title: 'Standard',
      price: 14999,
      minutes: 6000,
      features: features
    },
    {
      title: 'Premium',
      price: 29999,
      minutes: 12000,
      features: features,
      isPremium: true
    }
  ];

  const handleSavePlan = async (plan, price) => {
    if (!userId) {
      console.error('User ID is missing. Please ensure you are logged in.');
      return false;
    }

    setIsLoading(true);
    try {
      const requestData = {
        userId: userId,
        planDetails: {
          name: plan,
          price: price
        }
      };
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/plan`, requestData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      dispatch(setPricing({ plan, price }));
      dispatch(saveOnboardingState());

      setSavedPlans(prevSavedPlans => ({
        ...prevSavedPlans,
        [plan]: true
      }));

      setLastSavedPlan(plan);
      dispatch(setPricing({ plan, price }));
      return true;
    } catch (error) {
      console.error('Error submitting plan:', error);
      if (error.response) {
        console.error('Error response:', error.response);
      } else if (error.request) {
        console.error('Error request:', error.request);
      } else {
        console.error('Error:', error.message);
      }
      showToast('Failed to save plan. Please try again.', 'error');
      return false;
    } finally {
      setIsLoading(false);
    }
  };



  return (
    <div className={`max-w-6xl mx-auto px-4 mt-5 sm:px-6 lg:px-8 ${isDarkMode ? '' : 'bg-white'}`}>
      <motion.h1 
        className={`text-2xl md:text-4xl font-extrabold text-center mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        Pick a Plan
      </motion.h1>
      <motion.p 
        className={`text-lg text-center mb-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        Unlock the power of AI-driven communication for your business
      </motion.p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan, index) => (
          <PricingCard
            key={plan.title}
            {...plan}
            onSelect={handleSelectPlan}
            isSelected={selectedPlan?.plan === plan.title.toLowerCase()}
            isPremium={plan.title === 'Premium'}
          />
        ))}
      </div>
      {selectedPlan && (
        <>
          <motion.div 
            className={`fixed bottom-0 left-0 right-0 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center pb-10 pt-5">
              <div>
                <p className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                  Selected: {selectedPlan.plan.charAt(0).toUpperCase() + selectedPlan.plan.slice(1)} plan
                </p>
                <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  ₹{selectedPlan.price.toLocaleString()} / {plans.find(p => p.title.toLowerCase() === selectedPlan.plan)?.minutes.toLocaleString()} mins
                </p>
              </div>
            </div>
          </motion.div>
          <OnboardingButtonDock
            onSave={() => handleSavePlan(selectedPlan.plan, selectedPlan.price)}
            onBack={() => {
              dispatch(setCurrentStep(4)); // Go back to Integration
            }}
            onNext={() => {
              handleSavePlan(selectedPlan.plan, selectedPlan.price).then(() => {
                dispatch(setCurrentStep(6)); // Move to Summary
              });
            }}
            title={lastSavedPlan === selectedPlan?.plan ? 'Plan Saved' : 'Save Plan'}
            loading={isLoading}
            desktopClassName="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50"
            mobileClassName="fixed bottom-8 right-8 z-50"
          />
        </>
      )}
    </div>
  );
};

export default OnboardingPricing;
